import {DragAndDrop} from '@github-ui/drag-and-drop'
import {Checkbox, Spinner} from '@primer/react'
import {UnsafeHTMLDiv} from '@github-ui/safe-html/UnsafeHTML'
import type {OnConvertToIssueCallback, OnConvertToSubIssueCallback} from '@github-ui/markdown-viewer/types'
import {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react'
import type {TaskItem} from '../constants/types'
import {handleItemToggle} from '../utils/handle-item-toggle'
import styles from './TaskListItem.module.css'
import {TaskListMenu} from './TaskListMenu'

export type TaskListItemProps = {
  markdownValue: string
  onChange: (markdown: string) => void | Promise<void>
  onConvertToIssue?: OnConvertToIssueCallback
  onConvertToSubIssue?: OnConvertToSubIssueCallback
  nested?: boolean
  position?: number
  item: TaskItem
  totalItems: number
  disabled?: boolean
  // True only while a reorder/toggle save is in flight. Kept separate from
  // `disabled` so the task-options menu trigger can stay focusable during the
  // save while other controls disable.
  isUpdating?: boolean
  hideActions?: boolean
  itemIdToFocus?: string | number | null
  setItemIdToFocus?: React.Dispatch<React.SetStateAction<string | number | null>>
  toggledItem?: string | number | null
  setToggledItem: React.Dispatch<React.SetStateAction<string | number | null>>
  viewerCanCreateIssues?: boolean
}

const MATH_DOM_PURIFY_CONFIG = {
  ADD_TAGS: ['math-renderer'],
  ADD_ATTR: ['style', 'data-run-id', 'data-catalyst', 'class'],
}

const ISSUES_REGEX = /\/issues\/\d*\d/

// Check if there are references mixed in with any other content by rendering the content, removing the references
// and checking if there is any left over content
const isConvertable = (content: string, isIssueConversion: boolean) => {
  const renderedContent = document.createElement('div')
  // eslint-disable-next-line @github-ui/github-monorepo/require-set-sanitized-inner-html -- re-assigns HTML previously read from the live DOM (string)
  renderedContent.innerHTML = content
  const references = renderedContent.querySelectorAll('span.reference')
  // If there are 0 references, then it is just some form of text, so it is convertable
  if (references.length === 0) {
    return true
  }
  // If we are testing if this content is convertable to an issue, we return false if there are any references at all
  if (isIssueConversion) {
    return false
  }
  // If there is more than 1 reference, we can't know which reference to use in conversion, so it is not convertable
  if (references.length > 1) {
    return false
  }
  const onlyReference = references[0] as HTMLElement

  // If there is content left over after removing the reference, then it is mixed content and not convertable
  onlyReference.remove()
  if (renderedContent.innerHTML.trim() !== '') {
    return false
  }
  // We can only convert issues to sub-issues, so if the reference is not an issue, then it is not convertable
  if (!ISSUES_REGEX.test(onlyReference.innerHTML)) {
    return false
  }
  return true
}

export function TaskListItem({
  markdownValue,
  onChange,
  onConvertToIssue,
  onConvertToSubIssue,
  nested = false,
  position = 1,
  item,
  totalItems,
  disabled,
  isUpdating,
  hideActions,
  itemIdToFocus,
  setItemIdToFocus,
  toggledItem,
  setToggledItem,
  viewerCanCreateIssues = true,
}: TaskListItemProps) {
  const [checked, setChecked] = useState(item.checked)
  const [showTrigger, setShowTrigger] = useState(false)
  const [isConverting, setIsConverting] = useState(false)
  const checkboxRef = useRef<HTMLInputElement>(null)

  // Most controls are non-interactive while a save is in flight. The task-options
  // menu trigger is deliberately excluded so it can retain keyboard focus through
  // the async reorder (see `isUpdating` above).
  const interactionDisabled = disabled || isUpdating

  const handleOnDropFocus = (itemId: string | number) => {
    const newlyDroppedItem = document.getElementById(`task-list-menu-item-${itemId}`)
    if (newlyDroppedItem) {
      newlyDroppedItem.focus()
    }
  }

  // A reorder save round-trips the
  // same way as a toggle (below): NewMarkdownViewer replaces the markdown body
  // innerHTML and remounts every portal-mounted TaskListItem. Consume the focus
  // token on MOUNT — like `toggledItem` — rather than via a shared effect keyed on
  // `itemIdToFocus`, which also re-fires on the old item right before it unmounts
  // and clears the token before the remounted item can use it. This restores focus
  // to the moved item's options menu after the remount. The activeElement guard
  // avoids stealing focus if the user moved on during the async save.
  useLayoutEffect(() => {
    if (itemIdToFocus !== item.id || disabled) return
    const activeElement = document.activeElement
    if (activeElement === null || activeElement === document.body) {
      handleOnDropFocus(item.id)
    }
    setItemIdToFocus?.(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // See accessibility-audits#16153 (WCAG 2.4.3). When a toggle's save
  // round-trips, NewMarkdownViewer replaces the markdown body innerHTML and
  // every portal-mounted TaskListItem unmounts/re-mounts. The parent-held
  // `toggledItem` survives that cycle; on mount we consume it to re-focus the
  // new checkbox, then clear it so an unrelated future remount (e.g., a
  // collaborator's live update) doesn't redirect focus to a long-past toggle.
  // The activeElement guard prevents stealing focus if the user moved on
  // during the async save.
  useLayoutEffect(() => {
    if (toggledItem !== item.id || disabled) return
    const activeElement = document.activeElement
    if (activeElement === null || activeElement === document.body) {
      checkboxRef.current?.focus()
    }
    setToggledItem(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onToggleItem = useCallback(
    (markdown: string) => {
      setChecked(!item.checked)
      setToggledItem(item.id)

      handleItemToggle({markdownValue: markdown, markdownIndex: item.markdownIndex, onChange})
    },
    [item, onChange, setToggledItem],
  )

  const allowReordering =
    (!nested && !item.hasDifferentListTypes) ||
    (nested && position < 2 && !item.parentIsChecklist && !item.hasDifferentListTypes)

  const [canConvertToIssue, canConvertToSubIssue] = useMemo(
    () => [
      viewerCanCreateIssues && isConvertable(item.content, true),
      viewerCanCreateIssues && isConvertable(item.content, false),
    ],
    [item.content, viewerCanCreateIssues],
  )

  const tasklistIstemTestIdBase = `tasklist-item-${position}-${item.markdownIndex}`

  let tasklistItemCssClasses =
    nested && (!onConvertToIssue || !onConvertToSubIssue)
      ? styles['no-convert-task-list-item']
      : styles['task-list-item']

  tasklistItemCssClasses += ` ${nested && totalItems > 0 ? 'contains-task-list' : ''}`
  tasklistItemCssClasses += disabled ? ` ${styles.disabled}` : ''

  return (
    <div
      className={tasklistItemCssClasses}
      onMouseEnter={() => setShowTrigger(true)}
      onMouseLeave={() => setShowTrigger(false)}
      data-testid={tasklistIstemTestIdBase}
    >
      <div className={styles['left-aligned-content']}>
        <div className={styles['drag-drop-container']}>
          {allowReordering && !interactionDisabled && (
            <DragAndDrop.DragTrigger
              className={`${styles['drag-handle-icon']} ${showTrigger ? styles['show-trigger'] : ''}`}
              style={{
                width: '20px',
                height: '28px',
              }}
            />
          )}
        </div>

        <div className={styles['checkbox-items']} id={`checkbox-item-${item.id}`}>
          <Checkbox
            ref={checkboxRef}
            checked={checked}
            disabled={interactionDisabled}
            onChange={e => {
              e.preventDefault()
              e.stopPropagation()
              onToggleItem(markdownValue)
            }}
            aria-label={`${item.title} checklist item`}
            className={styles.taskStatusCheckbox}
          />
          <UnsafeHTMLDiv
            html={item.content}
            className={styles['task-list-html']}
            domPurifyConfig={MATH_DOM_PURIFY_CONFIG}
          />
        </div>
      </div>
      <div className={styles.TaskListItemActionsContainer}>
        {isConverting && <Spinner size="small" />}

        {!hideActions && allowReordering && item.position && !isConverting && (
          <TaskListMenu
            data-testid={`${tasklistIstemTestIdBase}-menu`}
            onConvertToIssue={onConvertToIssue}
            onConvertToSubIssue={onConvertToSubIssue}
            totalItems={totalItems}
            item={item}
            disabled={disabled}
            allowIssueConversion={canConvertToIssue}
            allowSubIssueConversion={canConvertToSubIssue}
            setIsConverting={setIsConverting}
            allowReordering={allowReordering}
            onMoveComplete={movedId => setItemIdToFocus?.(movedId)}
          />
        )}
      </div>
    </div>
  )
}
